<template>
  <div class="add_ded_container">
    <label>{{ label }}</label>
    <div class="rows_container">
      <div v-if="value.length > 0" class="row_container">
        <div class="heading">Type</div>
        <div class="heading">£</div>
      </div>
      <div
          v-if="!value || value.length === 0"
          class="row_container"
          style="padding-top: 1em;"
      >
        None
      </div>

      <div v-for="(item, index) in value" :key="index" class="row_container">
        <div>{{ descriptionLabel(item.add_ded_desc_id) || '—' }}</div>
        <div v-if="item.amount !== null">£{{ item.amount.toFixed(2) }}</div>
        <b-button
            v-if="!disabled"
            class="del_button"
            size="sm"
            variant="danger"
            @click="removeRow(index)"
            @keydown="handleDelKeyDown"
        >
          ✕
        </b-button>
      </div>

      <div v-if="!disabled" class="form">
        <addition_deductions_input
            ref="inputComponent"
            :descriptions="descriptions"
            :label="formLabel"
            :showAddButton="true"
            :value="draft"
            @input="updateDraft"
            @add="addRow"
            @advance="advanceFromMiniForm"
            @retreat="retreatFromMiniForm"
            @reset="resetAndFocus"
        />
      </div>
    </div>
  </div>
</template>

<script>
import addition_deductions_input from './addition_deductions_input.vue';

export default {
  components: { addition_deductions_input },
  props: {
    label: { required: true },
    value: { type: Array, required: true },
    descriptions: { type: Array, required: true },
    formLabel: { type: String, default: 'New Entry' },
    disabled: Boolean
  },
  data() {
    return {
      draft: { add_ded_desc_id: null, add_ded_type_id: null, amount: null }
    };
  },
  computed: {
    canAdd() {
      return (
          this.draft.add_ded_desc_id !== null &&
          this.draft.amount !== null &&
          !isNaN(this.draft.amount)
      );
    }
  },
  methods: {
    // --- internal helpers ---
    getDeleteButtons() {
      return Array.from(this.$el.querySelectorAll('.del_button'));
    },
    draftIncomplete() {
      return !this.canAdd;
    },

    // --- delete buttons keyboard flow ---
    handleDelKeyDown(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        this.resetAndFocus();
        return;
      }

      const isBack = e.key === 'ArrowLeft' || (e.key === 'Tab' && e.shiftKey);
      const isFwd = e.key === 'ArrowRight' || e.key === 'Tab';

      const buttons = this.getDeleteButtons();
      const idx = buttons.indexOf(e.currentTarget);

      if (isBack) {
        e.preventDefault();
        if (idx > 0) {
          buttons[idx - 1]?.focus?.();
          return;
        }
        if (this.draftIncomplete()) this.resetDraft();
        this.$emit('retreat'); // bubble to parent (previous section)
        return;
      }

      if (isFwd) {
        e.preventDefault();
        if (idx > -1 && idx < buttons.length - 1) {
          buttons[idx + 1]?.focus?.();
          return;
        }
        this.$refs.inputComponent?.focusDescInput(); // then into mini-form amount
      }
    },

    // --- expose first/last ✕ for parent entry points ---
    focusFirstDelete() {
      this.$nextTick(() => {
        const buttons = this.getDeleteButtons();
        if (buttons.length) {
          const btn = buttons[0];
          btn.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          btn.focus?.();
        } else {
          this.focusDescInput();
        }
      });
    },
    focusLastDelete() {
      this.$nextTick(() => {
        const buttons = this.getDeleteButtons();
        if (buttons.length) {
          const btn = buttons[buttons.length - 1];
          btn.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
          btn.focus?.();
        } else {
          // fallback when nothing to delete
          this.focusDescInput();
        }
      });
    },

    // --- mini-form event handlers ---
    updateDraft(val) {
      this.draft = { ...val };
    },

    advanceFromMiniForm() {
      if (this.draftIncomplete()) this.resetDraft();
      this.$emit('advance');
    },
    retreatFromMiniForm() {
      const buttons = this.getDeleteButtons();
      if (buttons.length) {
        const last = buttons[buttons.length - 1];
        last.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
        last.focus?.();
        return;
      }
      if (this.draftIncomplete()) this.resetDraft();
      this.$emit('retreat');
    },

    addRow(payload) {
      const toAdd = payload || this.draft;
      this.$emit('input', [...this.value, { ...toAdd }]);
      this.resetDraft();
      this.$nextTick(() => this.$refs.inputComponent?.focusDescInput());
    },

    removeRow(index) {
      const updated = [...this.value];
      updated.splice(index, 1);
      this.$emit('input', updated);
    },

    resetDraft() {
      this.draft = { add_ded_desc_id: null, add_ded_type_id: null, amount: null };
    },

    descriptionLabel(id) {
      const desc = this.descriptions.find(d => d.id === id);
      return desc ? desc.value : null;
    },

    resetAndFocus() {
      this.resetDraft();
      this.$nextTick(() => this.$refs.inputComponent?.focusDescInput());
    },

    focusAmountInput() {
      this.$refs.inputComponent?.focusAmountInput();
    },

    focusFirstInput() {
      this.$refs.inputComponent?.focusDescInput();
    },

    focusDescInput() {
      this.$refs.inputComponent?.focusDescInput();
    }
  }
};
</script>

<style lang="scss" scoped>
.add_ded_container { display: grid; grid-template-columns: 400px; }
@media(min-width: 1060px) { .add_ded_container { grid-template-columns: 200px auto; } }

.add_ded_container > label { font-weight: bold; margin-bottom: 1em; margin-top: 1em; }
.rows_container { margin-bottom: 0.5em; }

.row_container {
  display: grid;
  grid-template-columns: 180px 100px 50px;
  grid-gap: 10px;
  margin-bottom: 0.5em;
}
.row_container .heading { font-weight: bold; }

.form { display: grid; grid-template-columns: 350px; }

.add_button { height: 100%; width: 100%; background-color: #d6006e; }
.del_button { height: 33px; width: 100%; }

.btn:active { color: white; background-color: #d6006e; }
.btn-outline-info:active, .btn-info:hover { color: white !important; background-color: #e6007e !important; }
.btn-info { color: white; background-color: #e6007e; }
.btn-info:disabled { background-color: #ed969e; }
</style>
